import { useAuthStore, type EffectiveSessionGeneration } from '@/store/auth';

export const SessionSensitiveRequestNameConst = {
    profile: 'profile',
    activeRoleAssignment: 'activeRoleAssignment',
    impersonationContext: 'impersonationContext',
} as const;

export type SessionSensitiveRequestName =
    (typeof SessionSensitiveRequestNameConst)[keyof typeof SessionSensitiveRequestNameConst];

export class StaleEffectiveSessionResponseError extends Error {
    constructor(readonly requestName: SessionSensitiveRequestName) {
        super(`Ignored stale effective-session response for ${requestName}.`);
        this.name = 'StaleEffectiveSessionResponseError';
    }
}

type SessionSensitiveRequestFactory<Response> = () => Promise<Response>;

const inFlightSessionSensitiveRequests: Set<Promise<void>> = new Set();
let invalidatedRequestsBarrier: Promise<void> = Promise.resolve();

function isGenerationCurrent(generation: EffectiveSessionGeneration): boolean {
    return useAuthStore.getState().isEffectiveSessionGenerationCurrent(generation);
}

export function invalidateSessionSensitiveRequests(): void {
    const requestsToDrain: readonly Promise<void>[] = [...inFlightSessionSensitiveRequests];
    if (requestsToDrain.length === 0) {
        return;
    }

    invalidatedRequestsBarrier = invalidatedRequestsBarrier
        .catch((): void => undefined)
        .then(async (): Promise<void> => {
            await Promise.allSettled(requestsToDrain);
        });
}

export async function runSessionSensitiveRequest<Response>(
    requestName: SessionSensitiveRequestName,
    requestFactory: SessionSensitiveRequestFactory<Response>
): Promise<Response> {
    const requestGeneration: EffectiveSessionGeneration = useAuthStore
        .getState()
        .getEffectiveSessionGeneration();

    await invalidatedRequestsBarrier;
    if (!isGenerationCurrent(requestGeneration)) {
        throw new StaleEffectiveSessionResponseError(requestName);
    }

    const requestPromise: Promise<Response> = Promise.resolve().then(requestFactory);
    const trackedPromise: Promise<void> = requestPromise.then(
        (): void => undefined,
        (): void => undefined
    );
    inFlightSessionSensitiveRequests.add(trackedPromise);

    try {
        const response: Response = await requestPromise;
        if (!isGenerationCurrent(requestGeneration)) {
            throw new StaleEffectiveSessionResponseError(requestName);
        }
        return response;
    } finally {
        inFlightSessionSensitiveRequests.delete(trackedPromise);
    }
}
